import { useFormatter } from '@api/useFormatter'
import { DateRangeFilter } from '@components/_SiteWide/Grid/ActionButtons/DateRangeFilter'
import { usePersistentDateRangeFilter } from '@components/_SiteWide/Grid/ActionButtons/DateRangeFilter/usePersistentDateRangeFilter'
import { DownloadButton } from '@components/_SiteWide/Grid/ActionButtons/DownloadButton'
import type { FormResponse, FormResponseOverviewReq } from '@components/DriverManagement/DriverForms/api/schema'
import { useFormAPI } from '@components/DriverManagement/DriverForms/api/useFormsAPI'
import {
  ResponseDrawer,
  type ResponseDrawerPayload,
} from '@components/DriverManagement/DriverForms/components/ResponseDrawer'
import { dateFormat } from '@constants/dateFormat'
import { GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import React, { useMemo, useRef, useState } from 'react'

import { getColumnDefs } from './columnDefs'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'forms-resp-grid'

export function ResponseGrid() {
  const gridAPIRef = useRef(null)
  const { updateSetting } = useUserSettings()
  const { formatDecimal } = useFormatter()

  const [dateRange, storageValue, setDateRange] = usePersistentDateRangeFilter('form-response-day-ranges')
  const { useFormResponseOverview } = useFormAPI()

  const [drawerPayload, setDrawerPayload] = useState({ visible: false } as ResponseDrawerPayload)

  const columnDefs = getColumnDefs(formatDecimal, setDrawerPayload)

  const agPropOverrides = useMemo(() => {
    return {
      getRowId: (row) => row.data?.id,
      groupDisplayType: 'multipleColumns',
      suppressAggFuncInHeader: true,
    }
  }, [])

  const controlBarProps = useMemo(() => {
    return {
      title: 'Form Responses',
      actionButtons: (
        <Horizontal verticalCenter style={{ gap: '1em' }}>
          <DownloadButton fileName='Form Responses' gridAPIRef={gridAPIRef} />
          <DateRangeFilter
            currentFilters={dateRange}
            setCurrentFilters={setDateRange}
            fromKey='fromDate'
            toKey='toDate'
            getFromStorage={() => dateRange}
            dayRangeStorage={storageValue}
            setDayRangeStorage={setDateRange}
          />
        </Horizontal>
      ),
    }
  }, [dateRange])

  const overviewPayload: FormResponseOverviewReq = useMemo(() => {
    return {
      from_date: dateRange.fromDate.toISOString(),
      to_date: dateRange.toDate.toISOString(),
    }
  }, [dateRange])

  const {
    data,
    isPending: isLoadingOverview,
    isFetching: isFetchingOverview,
  } = useFormResponseOverview(overviewPayload) as {
    data: FormResponse[] | undefined
    isPending: boolean
    isFetching: boolean
  }

  return (
    <div style={{ height: '100%' }}>
      <GraviGrid
        columnDefs={columnDefs}
        agPropOverrides={agPropOverrides}
        controlBarProps={controlBarProps}
        primaryKey='id'
        storageKey={STORAGE_KEY}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        loading={isLoadingOverview || isFetchingOverview}
        rowData={data}
        externalRef={gridAPIRef}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
      <ResponseDrawer drawerPayload={drawerPayload} setDrawerPayload={setDrawerPayload} />
    </div>
  )
}
